import React, { useEffect, useState } from "react";
import { useFetchContext } from "../context/FetchContext";
import { useToast } from "../context/ToastContext";
import DashboardCard from "../components/dashboard/DashboardCard";
import YearlyOrdersChart, {
  YearlyOrderData,
} from "../components/dashboard/YearlyOrdersChart";

interface DashboardStats {
  warehousesCount: number;
  usersCount: number;
  ordersCount: number;
  shipmentsCount: number;
  ordersByWarehouse?: {
    warehouseId: number;
    warehouseName: string;
    count: string;
  }[];
  shipmentsByWarehouse?: {
    warehouseId: number;
    warehouseName: string;
    uniqueSold: string;
  }[];
}

const Dashboard: React.FC = () => {
  const { fetchData, state } = useFetchContext();
  const { showToast } = useToast();
  const [stats, setStats] = useState<DashboardStats>({
    warehousesCount: 0,
    usersCount: 0,
    ordersCount: 0,
    shipmentsCount: 0,
  });
  const [ordersData, setOrdersData] = useState<YearlyOrderData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch stats data
        const statsResponse = await fetchData<DashboardStats>(
          "/documents/dashboard/stats"
        );
        setStats(statsResponse.data);

        // Fetch orders data for chart
        const ordersResponse = await fetchData<YearlyOrderData[]>(
          "/documents/monthly-orders"
        );
        setOrdersData(ordersResponse.data);
      } catch (error) {
        console.error(
          "Errore nel caricamento dei dati della dashboard:",
          error
        );
        showToast(
          "Impossibile caricare i dati della dashboard. Riprova più tardi.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    // eslint-disable-next-line
  }, []);

  // Utilizziamo lo stato di loading locale o quello globale del context
  const isLoading = loading || state.isLoading;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Dashboard</h1>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <DashboardCard
              title="Magazzini"
              value={stats.warehousesCount}
              icon="warehouse"
              color="bg-blue-500"
            />
            <DashboardCard
              title="Utenti"
              value={stats.usersCount}
              icon="users"
              color="bg-green-500"
            />
            <DashboardCard
              title="Ordini"
              value={stats.ordersCount}
              icon="shopping-cart"
              color="bg-purple-500"
            />
            <DashboardCard
              title="Venduti (pz)"
              value={stats.shipmentsCount}
              icon="truck"
              color="bg-amber-500"
            />
          </div>

          <h2 className="text-xl font-semibold mb-4">Dettagli per Magazzino</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.ordersByWarehouse &&
              stats.ordersByWarehouse.map((warehouse) => {
                // Find corresponding shipment data
                const shipmentData = stats.shipmentsByWarehouse?.find(
                  (item) => item.warehouseId === warehouse.warehouseId
                );

                return (
                  <React.Fragment key={warehouse.warehouseId}>
                    <DashboardCard
                      title={`Ordini ${warehouse.warehouseName}`}
                      value={parseInt(warehouse.count)}
                      icon="shopping-cart"
                      color="bg-purple-400"
                    />
                    <DashboardCard
                      title={`Venduti ${warehouse.warehouseName} (pz)`}
                      value={
                        shipmentData ? parseInt(shipmentData.uniqueSold) : 0
                      }
                      icon="truck"
                      color="bg-amber-400"
                    />
                  </React.Fragment>
                );
              })}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">
              Ordini Mensili per Magazzino
            </h2>
            <div className="h-96">
              <YearlyOrdersChart data={ordersData} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
